import React, { FormEvent, useState } from 'react';
import { Card } from 'primereact/card';
import { Divider } from 'primereact/divider';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { FormField } from '../../../components/FormField/FormField';
import { useForm, FormValidators } from '../../../hooks/useForm';
import { useClient } from '../../../hooks/useClient';
import { useAuthContext } from '../../../context/AuthContext';
import { classNames } from 'primereact/utils';
import { PrimeIcons } from 'primereact/api';
import { CreateTicketData, TicketType, TicketSeverity } from '../../../types/ticket';
import { TICKET_TYPE_LABELS, TICKET_SEVERITY_LABELS } from '../constants/ticketLabels';
import { TicketTypesLegend } from './TicketTypesLegend';
import './CreateTicketForm.scss';

interface CreateTicketFormProps {
  onTicketCreated?: () => void;
  onCancel?: () => void;
}

const INITIAL_FORM: CreateTicketData = {
  title: '',
  description: '',
  type: 'bug',
  severity: 'medium',
};

const TYPE_ICONS: Record<string, string> = {
  bug: PrimeIcons.TIMES_CIRCLE,
  feature: PrimeIcons.STAR,
  modification: PrimeIcons.COG,
};

const TYPE_COLORS: Record<string, string> = {
  bug: 'var(--danger)',
  feature: 'var(--success)',
  modification: 'var(--info)',
};

const TICKET_TYPE_OPTIONS = [
  { label: `${TICKET_TYPE_LABELS.bug}`, value: 'bug' as TicketType, icon: TYPE_ICONS.bug, color: TYPE_COLORS.bug },
  { label: `${TICKET_TYPE_LABELS.feature}`, value: 'feature' as TicketType, icon: TYPE_ICONS.feature, color: TYPE_COLORS.feature },
  {
    label: `${TICKET_TYPE_LABELS.modification}`,
    value: 'modification' as TicketType,
    icon: TYPE_ICONS.modification,
    color: TYPE_COLORS.modification,
  },
];

const SEVERITY_OPTIONS = [
  { label: `${TICKET_SEVERITY_LABELS.low}`, value: 'low' as TicketSeverity },
  { label: `${TICKET_SEVERITY_LABELS.medium}`, value: 'medium' as TicketSeverity },
  { label: `${TICKET_SEVERITY_LABELS.high}`, value: 'high' as TicketSeverity },
  { label: `${TICKET_SEVERITY_LABELS.critical}`, value: 'critical' as TicketSeverity },
];

const VALIDATORS: FormValidators<CreateTicketData> = {
  title: (form) => {
    if (!form.title?.trim()) return 'El título es obligatorio';
    if (form.title.length < 5) return 'El título debe tener al menos 5 caracteres';
  },
  description: (form) => {
    if (!form.description?.trim()) return 'La descripción es obligatoria';
    if (form.description.length < 10) return 'La descripción debe tener al menos 10 caracteres';
  },
};

export const CreateTicketForm = ({ onTicketCreated, onCancel }: CreateTicketFormProps) => {
  const { post } = useClient({ throwOnError: true });
  const { user } = useAuthContext();
  const [files, setFiles] = useState<File[]>([]);
  const { form, setFormFields, validationErrors, validate, isSaving, setIsSaving, hasChanged, resetForm } =
    useForm<CreateTicketData>(INITIAL_FORM, false, VALIDATORS);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []) as File[];
    const validFiles = selectedFiles.filter((file) => {
      if (file.size > 50 * 1024 * 1024) {
        return false;
      }
      return true;
    });

    if (validFiles.length + files.length > 10) {
      return;
    }

    setFiles([...files, ...validFiles]);
    event.target.value = '';
  };

  const handleFileRemove = (file: File) => {
    setFiles(files.filter((f) => f !== file));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const newErrors = validate();
    if (newErrors || !user?.organizationId) return;

    setIsSaving(true);

    try {
      const formData = new FormData();
      formData.append('data', JSON.stringify(form));

      files.forEach((file) => {
        formData.append('files', file);
      });

      await post(`/organizations/${user.organizationId}/tickets`, {
        body: formData,
        successMessage: {
          summary: 'Ticket creado',
          detail: 'Tu ticket ha sido creado correctamente. Te notificaremos cuando haya actualizaciones.',
        },
        errorMessages: {
          summary: 'Error al crear el ticket',
          defaultDetail: 'No se ha podido crear el ticket',
        },
      });

      resetForm();
      setFiles([]);
      onTicketCreated?.();
    } catch (error) {
      console.error('Error creating ticket:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Card className='create-ticket-card'>
      <div className='card-header'>
        <i className='pi pi-ticket card-icon'></i>
        <h4>Nuevo ticket</h4>
      </div>
      <Divider />
      <p className='subtitle'>
        Crea un ticket para reportar un error, solicitar una mejora o pedir una modificación. Te notificaremos cuando
        haya actualizaciones.
      </p>

      <TicketTypesLegend />

      <form onSubmit={handleSubmit} className={classNames({ 'form-loading': isSaving })}>
        <FormField labelTitle='Título' error={validationErrors?.title} fullWidth required>
          <InputText
            value={form.title}
            onChange={(e) => setFormFields({ title: e.target.value })}
            invalid={!!validationErrors?.title}
            placeholder='Ej: Error al cargar el dashboard'
          />
        </FormField>

        <FormField labelTitle='Descripción' error={validationErrors?.description} fullWidth required>
          <InputTextarea
            value={form.description}
            onChange={(e) => setFormFields({ description: e.target.value })}
            invalid={!!validationErrors?.description}
            rows={7}
            autoResize
            placeholder='Describe el problema o solicitud con detalle...'
          />
        </FormField>

        <div className='form-row'>
          <FormField labelTitle='Tipo' fullWidth required>
            <Dropdown
              value={form.type}
              onChange={(e) => setFormFields({ type: e.value })}
              options={TICKET_TYPE_OPTIONS}
              optionLabel='label'
              optionValue='value'
              placeholder='Selecciona el tipo'
            />
          </FormField>

          <FormField labelTitle='Severidad' fullWidth required>
            <Dropdown
              value={form.severity}
              onChange={(e) => setFormFields({ severity: e.value })}
              options={SEVERITY_OPTIONS}
              optionLabel='label'
              optionValue='value'
              placeholder='Selecciona la severidad'
            />
          </FormField>
        </div>

        <FormField labelTitle='Archivos adjuntos (opcional)' fullWidth>
          <div className='file-upload-section'>
            <input
              type='file'
              id='file-upload'
              multiple
              accept='*/*'
              onChange={handleFileSelect}
              disabled={files.length >= 10 || isSaving}
              style={{ display: 'none' }}
            />
            <Button
              type='button'
              label='Seleccionar archivos'
              icon='pi pi-upload'
              onClick={() => document.getElementById('file-upload')?.click()}
              disabled={files.length >= 10 || isSaving}
              outlined
            />
            {files.length > 0 && (
              <div className='files-list'>
                {files.map((file, index) => (
                  <div key={index} className='file-item'>
                    <span className='file-name'>{file.name}</span>
                    <span className='file-size'>{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                    <Button
                      type='button'
                      icon='pi pi-times'
                      className='p-button-text p-button-danger p-button-sm'
                      onClick={() => handleFileRemove(file)}
                      disabled={isSaving}
                    />
                  </div>
                ))}
              </div>
            )}
            <small className='helper-text'>Máximo 10 archivos, 50MB cada uno</small>
          </div>
        </FormField>

        <div className='form-actions'>
          {onCancel && (
            <Button
              type='button'
              label='Cancelar'
              onClick={onCancel}
              className='cancel-button'
              disabled={isSaving}
              outlined
            />
          )}
          <Button
            disabled={!hasChanged}
            label='Crear ticket'
            type='submit'
            className='create-ticket-button'
            loading={isSaving}
            icon='pi pi-send'
          />
        </div>
      </form>
    </Card>
  );
};
